import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * BottomSheet - Mobile-first sliding sheet container matching Figma designs
 * @param {boolean} isOpen - Whether sheet is open
 * @param {function} onClose - Close handler
 * @param {string} title - Optional title header
 * @param {React.ReactNode} children - Content inside bottom sheet
 * @param {string} maxHeight - Max height string (e.g. '80vh', '450px')
 * @param {string} variant - 'modal' (with overlay) or 'persistent' (no overlay)
 * @param {boolean} isExpanded - Used for persistent variant to control height
 * @param {function} onToggle - Used for persistent variant to toggle expansion
 */
export function BottomSheet({ 
  isOpen = true, 
  onClose, 
  title, 
  date, 
  children, 
  maxHeight = 'calc(100dvh - 64px)', 
  className = '',
  variant = 'modal',
  isExpanded = true,
  onToggle
}) {
  if (variant === 'modal' && !isOpen) return null;

  const sheetContent = (
    <motion.div
      initial={variant === 'modal' ? { y: '100%' } : false}
      animate={{ y: variant === 'persistent' ? (isExpanded ? 0 : 'calc(100% - 116px)') : 0 }}
      exit={variant === 'modal' ? { y: '100%' } : undefined}
      transition={{ type: 'spring', damping: 25, stiffness: 220 }}
      drag={variant === 'modal' ? 'y' : false}
      dragConstraints={{ top: 0, bottom: 500 }}
      dragElastic={0.4}
      onDragEnd={(e, info) => {
        if (variant === 'modal' && (info.offset.y > 80 || info.velocity.y > 300) && onClose) {
          onClose();
        }
      }}
      style={{ 
        maxHeight: variant === 'persistent' ? (isExpanded ? 'calc(100dvh - 180px)' : '116px') : maxHeight,
        zIndex: 900
      }}
      className={`bry-bottom-sheet ${className}`}
    >
      {/* Top Drag Handle Indicator */}
      <div 
        className="bry-sheet-handle"
        onClick={() => variant === 'persistent' && onToggle && onToggle(!isExpanded)}
        style={{ cursor: variant === 'persistent' ? 'pointer' : 'grab' }}
      >
        <div className="bry-sheet-handle-pill" />
      </div>

      {/* Header if title provided */}
      {title && (
        <div className="bry-sheet-header">
          <div>
            <h3 className="bry-sheet-title" style={{ color: 'var(--bry-text-primary)' }}>{title}</h3>
            {date && <span className="bry-sheet-date" style={{ color: 'var(--bry-text-secondary)' }}>{date}</span>}
          </div>
          {variant === 'modal' && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white rounded-full transition-colors"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* Content Container */}
      <div className="bry-sheet-body" style={{ color: 'var(--bry-text-primary)' }}>
        {children}
      </div>
    </motion.div>
  );

  if (variant === 'persistent') {
    return sheetContent;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="bry-bottom-sheet-overlay fixed inset-0 z-50 flex flex-col justify-end bg-black/60 backdrop-blur-md">
          {/* Touch backdrop to close */}
          {onClose && (
            <div 
              className="flex-1 w-full" 
              onClick={onClose} 
              aria-label="Cerrar modal"
            />
          )}
          {sheetContent}
        </div>
      )}
    </AnimatePresence>
  );
}

export default BottomSheet;
